export default class SoundManager {
	constructor() {
		this.audioCtx = new AudioContext();
		this._volume = 1.0;

		this.gainNode      = this.audioCtx.createGain();
		this.makeupGainNode = this.audioCtx.createGain();
		this.compressorNode = this.audioCtx.createDynamicsCompressor();

		const t = this.audioCtx.currentTime;

		this.gainNode.gain.setValueAtTime(this.volumeToGain(this._volume), t);

		// Fixed moderate compression: gently reduces dynamic range so all sounds
		// play at a consistent, loud level. Threshold -24 dB, soft knee, 3:1 ratio.
		this.compressorNode.threshold.setValueAtTime(-24, t);
		this.compressorNode.knee.setValueAtTime(12, t);
		this.compressorNode.ratio.setValueAtTime(3, t);
		this.compressorNode.attack.setValueAtTime(0.003, t);
		this.compressorNode.release.setValueAtTime(0.25, t);

		// Makeup gain compensates for compression so 0 dBFS-normalised input
		// peaks near -2 dBFS at the output (threshold -24, ratio 3, knee 12
		// → ~12 dB of gain reduction at 0 dBFS → +6 dB makeup keeps us safe).
		this.makeupGainNode.gain.setValueAtTime(Math.pow(10, 6 / 20), t);

		// Chain: per-sound normalise → compressor → makeup → master volume → speakers
		this.compressorNode.connect(this.makeupGainNode);
		this.makeupGainNode.connect(this.gainNode);
		this.gainNode.connect(this.audioCtx.destination);

		// Per-sound nodes connect here as their output
		this.targetNode = this.compressorNode;

		this._resumeHandler = () => this.audioCtx.state === 'suspended' && this.audioCtx.resume();
		document.addEventListener('click', this._resumeHandler);
	}

	volumeToGain(volume) {
		const minDb = -60;
		const maxDb = 0;
		const db = minDb + (maxDb - minDb) * volume;
		return Math.pow(10, db / 20);
	}

	// volume: 0–1 where 1.0 = 100% (unity, no boost — attenuator only)
	setVolume(volume) {
		this._volume = Math.max(0, Math.min(1, volume));
		this.gainNode.gain.setTargetAtTime(this.volumeToGain(this._volume), this.audioCtx.currentTime, 0.01);
	}

	// Compute a gain factor that brings a buffer's peak to targetPeak.
	// Capped at maxBoost to avoid extreme amplification of near-silence.
	// Default maxBoost of 8 (18 dB) handles quiet TTS synthesis output.
	computeNormalizeGain(buffer, targetPeak = 1, maxBoost = 8) {
		let peak = 0;
		for(let ch = 0; ch < buffer.numberOfChannels; ch++) {
			const data = buffer.getChannelData(ch);
			for(let i = 0; i < data.length; i++) {
				const abs = Math.abs(data[i]);
				if(abs > peak) peak = abs;
			}
		}
		if(peak < 0.001) return 1;
		const gain = Math.min(targetPeak / peak, maxBoost);
		return gain;
	}

	destroy() {
		document.removeEventListener('click', this._resumeHandler);
		this.audioCtx.close();
	}
}
